import { Panel, trendClass, trendGlyph } from "../Panel"
import type { ForcePostureDetailedItem } from "../dashboard-types"
import { useRegion } from "../store"

function scoreColor(score: number): string {
  if (score >= 70) return "bg-red-500/20 text-red-400 border-red-500/40"
  if (score >= 50) return "bg-orange-500/20 text-orange-400 border-orange-500/40"
  if (score >= 30) return "bg-amber-500/20 text-amber-400 border-amber-500/40"
  return "bg-emerald-500/20 text-emerald-400 border-emerald-500/40"
}

function breakdownText(breakdown: Record<string, number>): string {
  return Object.entries(breakdown)
    .map(([k, v]) => `${k.toUpperCase()}: ${v.toLocaleString()}`)
    .join(" \u00B7 ")
}

export function ForcePostureDetailed({ items, demo }: { items: ForcePostureDetailedItem[]; demo?: boolean }) {
  const openRegion = useRegion((s) => s.openRegion)

  return (
    <Panel title="Trade Flow Posture" demo={demo} count={items.length} helpText="Corridor-level flow scores with modal breakdown">
      <div>
        {items.map((it) => (
          <div
            key={it.id}
            role="button"
            tabIndex={0}
            onClick={() => openRegion(it.corridor)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                openRegion(it.corridor)
              }
            }}
            className="px-3 py-2 border-b border-[color:var(--border)] last:border-b-0 flex items-center gap-3 cursor-pointer hover:bg-[color:var(--surface-hover)] transition-colors focus:outline-none focus:bg-[color:var(--surface-hover)]"
          >
            {/* Score circle */}
            <div className="flex items-center gap-1">
              <div className={`w-9 h-9 rounded-full border flex items-center justify-center text-sm font-bold tabular-nums ${scoreColor(it.score)}`}>
                {it.score}
              </div>
              <span className={`text-xs font-bold ${trendClass(it.trend)}`}>
                {trendGlyph(it.trend)}
              </span>
            </div>

            {/* Label + breakdown */}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[color:var(--text)] truncate">{it.label}</div>
              <div className="text-[10px] text-[color:var(--text-dim)] uppercase tracking-wider truncate">
                {breakdownText(it.breakdown)}
              </div>
            </div>

            {/* Signal count */}
            <div className="text-[10px] text-[color:var(--text-muted)] tabular-nums whitespace-nowrap">
              {it.signal_count} signals <span className="text-[color:var(--accent)]">&rarr;</span>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  )
}
